<template>
  <div class="a2p-main">
    <div class="a2p-eyebrow">All done</div>
    <h1 class="a2p-title">Your practice is texting-ready.</h1>
    <p class="a2p-sub">
      SMS setup is complete. Your registration is filed, your phone number is
      provisioned, and your messaging service is live. You can start sending
      appointment reminders right away.
    </p>

    <div class="a2p-section">
      <div class="a2p-section-head">
        <div class="a2p-section-eyebrow">Summary</div>
        <h2>What's ready</h2>
      </div>
      <div class="a2p-summary">
        <div class="row">
          <div class="icon">🛡️</div>
          <div class="body">
            <div class="title">A2P 10DLC registration</div>
            <div class="desc">Business compliance registration with the carriers.</div>
          </div>
          <div class="badge ok">{{ registrationStatus || 'Submitted' }}</div>
        </div>
        <div class="row">
          <div class="icon">📞</div>
          <div class="body">
            <div class="title">Phone numbers</div>
            <div class="desc">SMS-capable numbers provisioned under your subaccount.</div>
          </div>
          <div class="badge ok">{{ phoneNumberCount }} active</div>
        </div>
        <div class="row highlight">
          <div class="icon">💬</div>
          <div class="body">
            <div class="title">SMS messaging</div>
            <div class="desc">Ready to send appointment reminders and care updates.</div>
          </div>
          <div class="badge ok">Ready!</div>
        </div>
      </div>
    </div>

    <div class="a2p-section">
      <div class="a2p-section-head">
        <div class="a2p-section-eyebrow">What's next</div>
        <h2>Three things to try</h2>
      </div>
      <div class="a2p-next-steps">
        <div class="step">
          <div class="num">1</div>
          <div>
            <div class="title">Configure SMS templates</div>
            <div class="desc">Set up automated appointment reminders, recall messages, and templates in Teamio Settings.</div>
          </div>
        </div>
        <div class="step">
          <div class="num">2</div>
          <div>
            <div class="title">Start messaging patients</div>
            <div class="desc">From any patient profile, you can now send an appointment reminder or care update.</div>
          </div>
        </div>
        <div class="step">
          <div class="num">3</div>
          <div>
            <div class="title">Monitor delivery</div>
            <div class="desc">Track message delivery rates and patient engagement from your dashboard.</div>
          </div>
        </div>
        <div class="step">
          <div class="num">4</div>
          <div>
            <div class="title">Set up email sending</div>
            <div class="desc">
              Add a verified email sender so you can also reach patients by email — campaigns,
              reminders, and confirmations.
              <button type="button" class="step-action" @click="goToEmailSettings">Set up email &rarr;</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <A2PCallout type="info">
      <strong>Good to know:</strong> Your phone number is provisioned and ready now.
      Full SMS sending activates once Twilio and the carriers approve your registration
      — typically 1–3 business days. All messages route through your dedicated Twilio
      subaccount.
    </A2PCallout>

    <div class="a2p-actions">
      <button type="button" class="a2p-btn a2p-btn-secondary" @click="goToSettings">
        Configure SMS settings
      </button>
      <button type="button" class="a2p-btn a2p-btn-primary" @click="goToMyAccount">
        Return to My Account
      </button>
    </div>

    <div class="a2p-support">
      Need help? Contact Teamio support for assistance with SMS setup.
    </div>
  </div>
</template>

<script>
import A2PCallout from './components/A2PCallout.vue';

export default {
  name: 'A2PWizardComplete',
  components: { A2PCallout },
  props: {
    // Declared so the orchestrator's shared eyebrow prop doesn't leak onto the root
    // element via $attrs. This step keeps its own "All done" eyebrow text.
    eyebrow: { type: String, default: '' },
    registrationStatus: { type: String, default: 'Submitted' },
    phoneNumberCount: { type: Number, default: 0 },
    purchasedNumbers: { type: Array, default: () => [] },
  },
  methods: {
    goToMyAccount() { this.$router.push({ name: 'MyAccount' }); },
    goToSettings() { this.$router.push({ name: 'Settings', query: { section: 'sms' } }); },
    // Same deep-link mechanism as goToSettings — routes to the Settings page with a
    // section hint (the Email tab lives there for Super Admin + Admin).
    goToEmailSettings() { this.$router.push({ name: 'Settings', query: { section: 'email' } }); },
  },
};
</script>

<style src="./a2p-wizard.css"></style>

<style scoped>
.a2p-summary {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.a2p-summary .row {
  display: grid;
  grid-template-columns: 40px 1fr auto;
  gap: 14px;
  align-items: center;
  padding: 14px 18px;
  background: var(--a2p-surface);
  border: 1px solid var(--a2p-border);
  border-radius: var(--a2p-radius-m);
}
.a2p-summary .row.highlight {
  background: var(--a2p-success-soft);
  border-color: var(--a2p-success);
}
.a2p-summary .icon { font-size: 24px; }
.a2p-summary .title { font-weight: 600; color: var(--a2p-fg); font-size: 15px; }
.a2p-summary .desc { color: var(--a2p-fg-2); font-size: 13px; margin-top: 2px; }
.a2p-summary .badge {
  padding: 4px 12px;
  border-radius: 99px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
}
.a2p-summary .badge.ok {
  background: var(--a2p-success-soft);
  color: var(--a2p-success-ink);
}
.a2p-summary .row.highlight .badge { background: #fff; }

.a2p-next-steps {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.a2p-next-steps .step {
  display: grid;
  grid-template-columns: 36px 1fr;
  gap: 14px;
  align-items: start;
  padding: 14px 18px;
  background: var(--a2p-surface);
  border: 1px solid var(--a2p-border);
  border-radius: var(--a2p-radius-m);
}
.a2p-next-steps .num {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: var(--a2p-accent-soft);
  color: var(--a2p-accent-ink);
  display: grid;
  place-items: center;
  font-weight: 700;
  font-size: 13px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}
.a2p-next-steps .title { font-weight: 600; font-size: 14px; color: var(--a2p-fg); }
.a2p-next-steps .desc { color: var(--a2p-fg-2); font-size: 13px; line-height: 1.55; margin-top: 3px; }
.a2p-next-steps .step-action {
  display: inline;
  margin-left: 4px;
  padding: 0;
  background: transparent;
  border: none;
  color: var(--a2p-accent);
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.a2p-next-steps .step-action:hover { text-decoration: underline; }

.a2p-support {
  text-align: center;
  margin-top: 28px;
  padding-top: 20px;
  border-top: 1px solid var(--a2p-border);
  font-size: 12px;
  color: var(--a2p-fg-3);
}
</style>
